/**
 * Per-page SEO head with canonical / OG / Twitter / GA4. Used on every public page.
 * For article pages also pass `jsonLd` (NewsArticle schema).
 */
import React from "react";
import { Helmet } from "react-helmet-async";

function pickCanonical(site) {
  if (!site) return window.location.href;
  if (site.domain) {
    // Real custom domain (post-Cloudflare-Worker) — preserve the path.
    const { pathname, search } = window.location;
    // Strip the /site/{slug} preview prefix because real domain serves root.
    const stripped = pathname.replace(/^\/site\/[^/]+/, "");
    return `https://${site.domain}${stripped || "/"}${search || ""}`;
  }
  return window.location.href;
}

export default function SeoHead({
  site,
  title,
  description,
  image,
  type = "website",
  jsonLd,
  noindex = false,
}) {
  if (!site) return null;
  const canonical = pickCanonical(site);
  const fullTitle = title
    ? `${title} — ${site.name}`
    : (site.name + (site.tagline ? " — " + site.tagline : ""));
  const desc = description || site.tagline || site.description || `${site.name} — latest news, analysis, and reporting.`;
  const ogImage = image || site.publisher_logo_url || undefined;
  const ga4 = site.ga4_measurement_id;
  const twitterHandle = site.twitter_handle;
  return (
    <Helmet prioritizeSeoTags>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      <link rel="canonical" href={canonical} />
      {noindex && <meta name="robots" content="noindex,nofollow" />}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={canonical} />
      <meta property="og:site_name" content={site.name} />
      {ogImage && <meta property="og:image" content={ogImage} />}
      <meta name="twitter:card" content={ogImage ? "summary_large_image" : "summary"} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      {ogImage && <meta name="twitter:image" content={ogImage} />}
      {twitterHandle && <meta name="twitter:site" content={twitterHandle} />}
      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      )}
      {ga4 && (
        <script async src={`https://www.googletagmanager.com/gtag/js?id=${ga4}`}></script>
      )}
      {ga4 && (
        <script>
          {`window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());gtag('config','${ga4}',{anonymize_ip:true});`}
        </script>
      )}
    </Helmet>
  );
}

export function buildNewsArticleJsonLd({ site, article, canonical }) {
  const publisherName = site.publisher_name || site.name;
  return {
    "@context": "https://schema.org",
    "@type": "NewsArticle",
    "headline": article.title || article.original_title,
    "description": article.summary || article.seo_description,
    "image": article.image_url ? [article.image_url] : undefined,
    "datePublished": article.published_at,
    "dateModified": article.published_at,
    "mainEntityOfPage": { "@type": "WebPage", "@id": canonical },
    "publisher": {
      "@type": "Organization",
      "name": publisherName,
      "logo": site.publisher_logo_url ? {
        "@type": "ImageObject",
        "url": site.publisher_logo_url,
      } : undefined,
    },
    "author": {
      "@type": "Organization",
      "name": publisherName,
    },
    "articleSection": article.category_slug,
    "keywords": (article.seo_keywords || []).join(", "),
    "isAccessibleForFree": true,
  };
}
